<script lang="ts">
  import { createApi } from "./lib/api";
  import Login from "./views/Login.svelte";
  import Orgs from "./views/Orgs.svelte";
  import Users from "./views/Users.svelte";
  import Settings from "./views/Settings.svelte";
  import AppDownload from "./views/AppDownload.svelte";
  import Releases from "./views/Releases.svelte";
  import Snapshots from "./views/Snapshots.svelte";
  import Prospects from "./views/Prospects.svelte";
  import { Button, Icon, DialogHost, themeStore, toggleTheme } from "@price-check/ui";
  import { LayoutGrid, Users as UsersIcon, Gem, Sun, Moon, Settings as SettingsIcon, Download, RotateCcw, Camera, PhoneCall } from "lucide";
  import type { IconNode } from "lucide";
  import { seg, navigate, initRouter } from "./lib/route.svelte";

  const TOKEN_KEY = "pc_portal_token";
  const initialToken = localStorage.getItem(TOKEN_KEY);
  let token = $state<string | null>(initialToken);
  let email = $state("");
  let userRole = $state<"portal" | "app" | null>(null);
  let authLoading = $state(initialToken != null);
  type Tab = "orgs" | "users" | "prospects" | "snapshots" | "releases" | "settings" | "appDownload";

  initRouter();

  // The first URL segment IS the section — refreshing or sharing a link lands on
  // the same place. Anything unknown falls back to the role's default section.
  const TAB_BY_SLUG: Record<string, Tab> = {
    orgs: "orgs",
    users: "users",
    prospects: "prospects",
    snapshots: "snapshots",
    releases: "releases",
    downloads: "appDownload",
    settings: "settings",
  };
  const tab = $derived<Tab>(
    userRole === "app" ? "appDownload" : (TAB_BY_SLUG[seg(0) ?? ""] ?? "orgs"),
  );

  // A fresh client whenever the token changes (login / logout).
  const api = $derived(createApi("", token));

  // Resolve the signed-in account. Any valid app login can enter the portal; app
  // users are limited to downloads, while portal users get the owner tools.
  $effect(() => {
    if (!token) {
      email = "";
      userRole = null;
      authLoading = false;
      return;
    }
    let cancelled = false;
    authLoading = true;
    api
      .me()
      .then((m) => {
        if (cancelled) return;
        email = m.user.email ?? "";
        userRole = m.user.role === "portal" ? "portal" : "app";
        if (m.user.role !== "portal") navigate("/downloads", { replace: true });
        else if (!TAB_BY_SLUG[seg(0) ?? ""]) navigate("/orgs", { replace: true });
      })
      .catch(() => {
        if (cancelled) return;
        logout();
      })
      .finally(() => {
        if (!cancelled) authLoading = false;
      });
    return () => {
      cancelled = true;
    };
  });

  type NavItem = { id: Tab; label: string; icon: IconNode; path: string };
  const PORTAL_NAV: NavItem[] = [
    { id: "orgs", label: "Organizations", icon: LayoutGrid, path: "/orgs" },
    { id: "users", label: "Users", icon: UsersIcon, path: "/users" },
    { id: "prospects", label: "Prospects", icon: PhoneCall, path: "/prospects" },
    { id: "snapshots", label: "Snapshots", icon: Camera, path: "/snapshots" },
    { id: "releases", label: "Releases", icon: RotateCcw, path: "/releases" },
    { id: "appDownload", label: "App Download", icon: Download, path: "/downloads" },
    { id: "settings", label: "Settings", icon: SettingsIcon, path: "/settings" },
  ];
  const DOWNLOAD_NAV: NavItem[] = [
    { id: "appDownload", label: "App Download", icon: Download, path: "/downloads" },
  ];
  const nav = $derived(userRole === "portal" ? PORTAL_NAV : DOWNLOAD_NAV);

  function onLogin(t: string) {
    localStorage.setItem(TOKEN_KEY, t);
    authLoading = true;
    token = t;
  }
  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    email = "";
    userRole = null;
    authLoading = false;
    token = null;
    navigate("/", { replace: true });
  }
</script>

{#if !token}
  <Login {api} {onLogin} />
{:else if authLoading}
  <div class="min-h-screen grid place-items-center bg-page p-6">
    <div class="text-sm text-muted">Loading portal…</div>
  </div>
{:else}
  <div class="grid grid-cols-[248px_1fr] min-h-screen max-[760px]:grid-cols-1">
    <aside
      class="flex flex-col gap-2 px-3.5 py-5 bg-surface border-r border-border sticky top-0 h-screen
        max-[760px]:static max-[760px]:h-auto max-[760px]:flex-row max-[760px]:flex-wrap
        max-[760px]:items-center"
    >
      <div class="flex items-center gap-2.75 pl-2 pr-2 pt-1.5 pb-4.5 max-[760px]:p-0 max-[760px]:pr-2">
        <span
          class="grid place-items-center w-8.5 h-8.5 rounded-lg text-on-brand
            bg-[linear-gradient(135deg,var(--color-brand),var(--color-brand-strong))]
            shadow-md"
          aria-hidden="true"
        >
          <Icon icon={Gem} size={16} />
        </span>
        <div class="flex flex-col leading-tight">
          <strong class="text-md font-extrabold">Pretaro</strong>
          <span class="text-2xs text-muted">{userRole === "portal" ? "owner console" : "app downloads"}</span>
        </div>
      </div>
      <nav class="flex flex-col gap-0.75 max-[760px]:flex-row max-[760px]:flex-wrap">
        {#each nav as item (item.id)}
          <Button
            variant="ghost"
            class="w-full justify-start gap-2.75 px-2.75 py-2.25 text-left text-sm font-semibold {tab === item.id ? 'bg-brand-soft text-brand-strong' : ''}"
            onclick={() => navigate(item.path)}
          >
            <span class="w-4.5 grid place-items-center" aria-hidden="true">
              <Icon icon={item.icon} size={15} />
            </span>
            {item.label}
          </Button>
        {/each}
      </nav>
      <div
        class="mt-auto pt-3 border-t border-border max-[760px]:mt-0 max-[760px]:ml-auto
          max-[760px]:p-0 max-[760px]:border-none"
      >
        <Button variant="outline" size="sm" class="w-full max-[760px]:w-auto" onclick={logout}>
          Sign out
        </Button>
      </div>
    </aside>

    <div class="min-w-0 flex flex-col">
      <header
        class="flex items-center gap-3 px-8 py-5 border-b border-border sticky top-0 z-10
          bg-page/80 backdrop-blur-sm max-[760px]:px-4.5"
      >
        <h1 class="m-0 text-lg font-extrabold tracking-tight">
          {nav.find((n) => n.id === tab)?.label}
        </h1>
        <span
          class="ml-auto text-xs text-muted bg-surface border border-border px-2.75 py-1.25
            rounded-full"
        >
          {email ? `Signed in as ${email}` : "Signed in"}
        </span>
        <Button
          variant="outline"
          size="icon-sm"
          onclick={toggleTheme}
          aria-label={themeStore.resolved === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          <Icon icon={themeStore.resolved === "dark" ? Sun : Moon} size={15} />
        </Button>
      </header>
      <main class="px-8 pt-7 pb-14 max-w-[1080px] w-full max-[760px]:px-4.5">
        {#if tab === "orgs"}
          <Orgs {api} />
        {:else if tab === "users"}
          <Users {api} />
        {:else if tab === "prospects"}
          <Prospects {api} />
        {:else if tab === "snapshots"}
          <Snapshots {api} />
        {:else if tab === "releases"}
          <Releases {api} />
        {:else if tab === "appDownload"}
          <AppDownload />
        {:else}
          <Settings {email} />
        {/if}
      </main>
    </div>
  </div>
{/if}

<!-- App-wide dialog host: powers dialogs.alert/confirm/prompt(). Portals to body. -->
<DialogHost />
